import React from 'react';
import Text from '../../shared/Text';
import Icon from '../../shared/Icon';
import Image from '../../shared/Image';
import Heading from '../../shared/Heading';
import TypeaheadOption from './components/TypeaheadOption';
import {
  ImportForm,
  TypeaheadOption as TypeaheadOptionProps,
} from './components/ImportForm';
import { Footer } from '../../components/Footer';
import { useBrowserSize } from '../../contexts/BrowserSize';
import { valueProps, stepsToSwitch, quotes, faqs } from './constants';
import {
  IconContainer,
  Video,
  List,
  Container,
  SearchSection,
  SearchSectionBackground,
  SearchContent,
  PodcastSection,
  PodcastContent,
  WhySwitchSection,
  WhySwitchContent,
  ListContainer,
  ImageContainer,
  HowToSwitchSection,
  HowToSwitchContent,
  StepsContainer,
  StepContainer,
  StepTitle,
  QuotesSection,
  QuotesContent,
  QuotesContainer,
  QuoteContainer,
  QuoteHeader,
  QuoteImage,
  QuoteTitle,
  Quote,
  QuoteIconContainer,
  QuoteIcon,
  FaqSection,
  FaqBackground,
  FaqContent,
} from './styles';

type ImportScreenProps = {
  onSubmit: (param: string) => void;
  onBlur: () => void;
  isSearching: boolean;
  onSelectTypeahead: (options: TypeaheadOptionProps[]) => void;
  onTypeaheadSearch: (value: string) => void;
  rssTypeaheadResults: TypeaheadOptionProps[];
  rssFeedError: {
    message: string;
  };
  fetchingRSSFeedImport: boolean;
  rssFeedIsValid: boolean;
  rssFeedMetadata: {
    authorName: string;
    authorEmail: string;
    image: string;
    isExplicit: boolean;
    itunesCategory: string;
    language: string;
    podcastName: string;
    podcastDescription: string;
    episodeCount: number;
  };
  rssFeedUrl: string;
};

function ImportScreen({
  isSearching,
  rssTypeaheadResults,
  onTypeaheadSearch,
  onSelectTypeahead,
  onSubmit,
  fetchingRSSFeedImport,
  rssFeedUrl,
  rssFeedIsValid,
  rssFeedError,
  rssFeedMetadata,
}: ImportScreenProps) {
  const { width } = useBrowserSize();
  const hasDataToContinue: boolean =
    !fetchingRSSFeedImport && rssFeedMetadata && !!rssFeedMetadata.podcastName;

  const isMobile = width <= 768;
  const imageType = isMobile ? 'mobile' : 'desktop';
  return (
    <Container>
      <SearchSection>
        <SearchSectionBackground>
          <SearchContent>
            <div style={{ paddingBottom: 40, paddingTop: 75 }}>
              <Heading size="lg" color="#ffffff" align="center" isBold={true}>
                Switch your podcast to Anchor
              </Heading>
              <div style={{ marginTop: 10 }}>
                <Text align="center" color="#ffffff" size="lg">
                  Free hosting, automatic distribution, easy monetization.
                </Text>
              </div>
            </div>
            <ImportForm
              isSearching={isSearching}
              onSelectTypeahead={onSelectTypeahead}
              onTypeaheadSearch={onTypeaheadSearch}
              rssTypeaheadResults={rssTypeaheadResults}
              renderTypeaheadOption={option => {
                const { image, podcastName, authorName } = option;
                return (
                  <TypeaheadOption
                    image={image}
                    podcastName={podcastName}
                    authorName={authorName}
                  />
                );
              }}
              rssFeedError={rssFeedError}
              hasDataToContinue={hasDataToContinue}
              fetchingRSSFeedImport={fetchingRSSFeedImport}
              rssFeedIsValid={rssFeedIsValid}
              onClickGetStarted={() => {
                onSubmit(rssFeedUrl);
              }}
              rssFeedMetadata={rssFeedMetadata}
            />
          </SearchContent>
        </SearchSectionBackground>
      </SearchSection>
      <PodcastSection>
        <PodcastContent>
          <div style={{ marginBottom: 18 }}>
            <Heading size="md" align="center" isBold={true}>
              Anchor powers 1 in 3 of all new podcasts created in the world
            </Heading>
          </div>
          <Image
            imageUrl={`ttps://d12xoj7p9moygp.cloudfront.net/images/screens/import/notable_podcasts_${imageType}.png`}
            retinaImageUrl={`https://d12xoj7p9moygp.cloudfront.net/images/screens/import/notable_podcasts_${imageType}@2x.png`}
            width="100%"
            altText="Cover art for the podcasts Couples Therapy, Disect, Pretty Big Deal, Hype Radio, The Pat McAfee Show, and the A24 Podcast"
          />
        </PodcastContent>
      </PodcastSection>
      <WhySwitchSection>
        <WhySwitchContent>
          <ListContainer>
            <div style={{ paddingTop: 8, paddingBottom: 8 }}>
              <Heading size="md" isBold={true}>
                Why switch to Anchor?
              </Heading>
            </div>
            <List>
              {valueProps.map(valueProp => (
                <li key={`reasons-${valueProp}`}>
                  <Text size="lg" color="rgba(41, 47, 54, 0.6)">
                    {valueProp}
                  </Text>
                </li>
              ))}
            </List>
          </ListContainer>
          <ImageContainer>
            <Image
              imageUrl="https://d12xoj7p9moygp.cloudfront.net/images/screens/import/macbook_dashboard.png"
              retinaImageUrl="https://d12xoj7p9moygp.cloudfront.net/images/screens/import/macbook_dashboard@2x.png"
              width="100%"
              altText="A laptop displaying a podcast's analytics dashboard"
            />
          </ImageContainer>
        </WhySwitchContent>
      </WhySwitchSection>
      <HowToSwitchSection>
        <HowToSwitchContent>
          <Video>
            <iframe
              title="How to switch to Anchor"
              width="680"
              height="368"
              src="https://www.youtube.com/embed/dPA5BVe88nM"
              frameBorder="0"
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen={true}
            />
          </Video>
          <div style={{ paddingTop: 8, paddingBottom: 15 }}>
            <Heading size="md" align="center" isBold={true}>
              How switching works
            </Heading>
          </div>
          <StepsContainer>
            {stepsToSwitch.map((step, index) => (
              <StepContainer key={step.title}>
                <IconContainer>
                  <Image
                    imageUrl={step.imageUrl}
                    retinaImageUrl={step.retinaImageUrl}
                    width={100}
                    height={100}
                    altText={`Step ${index + 1} icon`}
                  />
                </IconContainer>
                <StepTitle>
                  <Heading
                    size="xs"
                    align="center"
                    color="#292f36"
                    isBold={true}
                  >
                    {`${index + 1}. ${step.title}`}
                  </Heading>
                </StepTitle>
                <div>
                  <Text size="md" align="center" color="rgba(41, 47, 54, 0.6)">
                    {step.description}
                  </Text>
                </div>
              </StepContainer>
            ))}
          </StepsContainer>
        </HowToSwitchContent>
      </HowToSwitchSection>
      <QuotesSection>
        <QuotesContent>
          <div style={{ paddingTop: 8, paddingBottom: 40 }}>
            <Heading size="md" align="center" color=" #292f36" isBold={true}>
              What other podcasters are saying about Anchor
            </Heading>
          </div>
          <QuotesContainer>
            {quotes.map(quote => (
              <QuoteContainer key={quote.podcastName}>
                <QuoteHeader>
                  <QuoteImage>
                    <Image
                      imageUrl={quote.podcastCoverartImageUrl}
                      retinaImageUrl={quote.podcastCoverartImageRetinaUrl}
                      width={50}
                      height={50}
                      altText={`Cover art for podcast ${quote.podcastName}`}
                    />
                  </QuoteImage>
                  <QuoteTitle>
                    <Text size="lg" align="left" color="gray">
                      {`${quote.authorName} from`}
                    </Text>
                    <Text size="lg" align="left" color="#292f36" isBold={true}>
                      {quote.podcastName}
                    </Text>
                  </QuoteTitle>
                </QuoteHeader>
                <Quote>
                  <Text size="lg" align="left" color="rgba(41, 47, 54, 0.6)">
                    {quote.quote}
                  </Text>
                </Quote>
                <QuoteIconContainer>
                  <QuoteIcon>
                    <Icon type="quote" fillColor="#8940fa" />
                  </QuoteIcon>
                </QuoteIconContainer>
              </QuoteContainer>
            ))}
          </QuotesContainer>
        </QuotesContent>
      </QuotesSection>
      <FaqSection>
        <FaqBackground>
          <FaqContent>
            <div style={{ paddingTop: 8, paddingBottom: 8, marginBottom: 37 }}>
              <Heading size="md" align="center" color="#ffffff" isBold={true}>
                Anchor FAQs
              </Heading>
            </div>
            <div>
              {faqs.map(faq => (
                <div key={faq.question}>
                  <div style={{ paddingTop: 8, paddingBottom: 8 }}>
                    <Heading
                      size="sm"
                      align="left"
                      color="#ffffff"
                      isBold={true}
                    >
                      {faq.question}
                    </Heading>
                  </div>
                  <div
                    style={{
                      paddingTop: 8,
                      paddingBottom: 8,
                      marginBottom: 20,
                    }}
                  >
                    {faq.renderAnswer()}
                  </div>
                </div>
              ))}
            </div>
          </FaqContent>
        </FaqBackground>
      </FaqSection>
      <Footer />
    </Container>
  );
}

export { ImportScreen };
